import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchDocuments } from "../services/erpDocuments";
import { getFirebaseToken } from "../utils/getFirebaseToken";

export default function DocumentListPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    doc_type: "",
    status: "",
    date_from: "",
    date_to: "",
  });

  const load = async () => {
  setLoading(true);
  setError("");
  try {
    const token = await getFirebaseToken();
    const data = await fetchDocuments(token, filters);
    setRows(data.rows || data.data || data || []);
  } catch (e) {
    console.error(e);
    setError(e?.response?.data?.message || e.message || "Failed to load documents.");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    load();
  }, []);

  const setField = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  function formatDateOnly(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold">ERP Documents</h1>
          <p className="text-slate-600">Create, review and post ERP documents.</p>
        </div>

        <Link
          to="/erp/documents/new"
          className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-black text-white"
        >
          New Document
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Document Type</label>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={filters.doc_type}
              onChange={(e) => setField("doc_type", e.target.value)}
            >
              <option value="">All</option>
              <option value="GOODS_RECEIPT">GOODS_RECEIPT</option>
              <option value="GOODS_ISSUE">GOODS_ISSUE</option>
              <option value="TRANSFER">TRANSFER</option>
              <option value="ASSET_ISSUE">ASSET_ISSUE</option>
              <option value="ASSET_RETURN">ASSET_RETURN</option>
              <option value="ADJUSTMENT">ADJUSTMENT</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={filters.status}
              onChange={(e) => setField("status", e.target.value)}
            >
              <option value="">All</option>
              <option value="DRAFT">DRAFT</option>
              <option value="SUBMITTED">SUBMITTED</option>
              <option value="APPROVED">APPROVED</option>
              <option value="POSTED">POSTED</option>
              <option value="CANCELLED">CANCELLED</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Date From</label>
            <input
              type="date"
              className="w-full border rounded-lg px-3 py-2"
              value={filters.date_from}
              onChange={(e) => setField("date_from", e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Date To</label>
            <input
              type="date"
              className="w-full border rounded-lg px-3 py-2"
              value={filters.date_to}
              onChange={(e) => setField("date_to", e.target.value)}
            />
          </div>

          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={load}
              className="w-full px-4 py-2 rounded-lg bg-black text-white"
            >
              Search
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3">
          {error}
        </div>
      ) : null}

      <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="bg-slate-100 text-left">
              <th className="border px-3 py-2">Doc No</th>
              <th className="border px-3 py-2">Type</th>
              <th className="border px-3 py-2">Status</th>
              <th className="border px-3 py-2">Doc Date</th>
              <th className="border px-3 py-2">Posting Date</th>
              <th className="border px-3 py-2">Currency</th>
              <th className="border px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="border px-3 py-4 text-center">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="border px-3 py-4 text-center text-slate-500">
                  No documents found.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  <td className="border px-3 py-2">{row.doc_no || row.id}</td>
                  <td className="border px-3 py-2">{row.doc_type}</td>
                  <td className="border px-3 py-2">{row.status}</td>
                  <td className="border px-3 py-2">{formatDateOnly(row.doc_date)}</td>
                  <td className="border px-3 py-2">{formatDateOnly(row.posting_date)}</td>
                  <td className="border px-3 py-2">{row.currency || ""}</td>
                  <td className="border px-3 py-2">
                    <Link
                      to={`/erp/documents/${row.id}`}
                      className="inline-flex px-3 py-1 rounded-lg border"
                    >
                      Open
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}